import React from 'react';
import {
  getForecastSource,
  setForecastSource,
  getForecastLocationId,
  setForecastLocationId,
  useForecastSourceVersion,
} from '../state/forecastSource';

interface ForecastSourceToggleProps {
  locations: Array<{ id: string; name: string }>;
  locationsLoading: boolean;
  hasHourlyToken: boolean;
}

export default function ForecastSourceToggle({
  locations,
  locationsLoading,
  hasHourlyToken,
}: ForecastSourceToggleProps) {
  useForecastSourceVersion(); // re-render on toggle
  const source = getForecastSource();
  const locationId = getForecastLocationId();

  if (!hasHourlyToken) return null;

  const selectedLocation = locations.find((l) => l.id === locationId);
  const statusLabel =
    source === 'live' && selectedLocation
      ? `Live: ${selectedLocation.name}`
      : source === 'live'
      ? 'Live'
      : 'Demo mode';

  return (
    <div className="flex items-center gap-2">
      {/* Demo / Live pill toggle */}
      <div className="flex rounded-md border border-gray-300 overflow-hidden text-xs">
        <button
          type="button"
          onClick={() => setForecastSource('demo')}
          className={`px-3 py-1 font-medium transition-colors ${
            source === 'demo'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          Demo
        </button>
        <button
          type="button"
          onClick={() => setForecastSource('live')}
          className={`px-3 py-1 font-medium transition-colors border-l border-gray-300 ${
            source === 'live'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          Live
        </button>
      </div>

      {/* Location dropdown — only shown in Live mode */}
      {source === 'live' && (
        <select
          value={locationId ?? ''}
          onChange={(e) => setForecastLocationId(e.target.value || null)}
          disabled={locationsLoading}
          className="text-xs border border-gray-300 rounded px-2 py-1 text-gray-700 focus:outline-none focus:border-blue-500 bg-white"
        >
          <option value="">
            {locationsLoading ? 'Loading…' : 'Select location'}
          </option>
          {locations.map((loc) => (
            <option key={loc.id} value={loc.id}>
              {loc.name}
            </option>
          ))}
        </select>
      )}

      {/* Status badge */}
      <span
        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          source === 'live'
            ? 'bg-green-50 text-green-700'
            : 'bg-gray-100 text-gray-500'
        }`}
      >
        {statusLabel}
      </span>
    </div>
  );
}
